import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Palette, Type, Image, Save, RotateCcw, Upload, Check, AlertTriangle, Eye, Columns, Sun, Moon, Sparkles, Zap, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { useTheme } from '@/context/ThemeContext';
import { Switch } from '@/components/ui/switch';

const HEADING_FONTS = [
  'Poppins', 'Montserrat', 'Playfair Display', 'Merriweather', 'Oswald',
  'Plus Jakarta Sans', 'Urbanist', 'Space Grotesk', 'Sora', 'Manrope',
  'Raleway', 'Quicksand', 'Outfit'
];
const BODY_FONTS = [
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Nunito',
  'DM Sans', 'Source Sans 3', 'Work Sans', 'Karla', 'Urbanist',
  'Plus Jakarta Sans', 'Sora', 'Manrope'
];

const DEFAULT_THEME = {
  colors: {
    primary: '#2563EB',
    secondary: '#111827',
    accent: '#F59E0B',
    background: '#FFFFFF',
    surface: '#F9FAFB',
    text: '#111827',
    mutedText: '#6B7280',
    border: '#E5E7EB'
  },
  fonts: {
    headingFont: 'Poppins',
    bodyFont: 'Inter'
  },
  assets: {
    logoUrl: null,
    logoAlt: 'Graphikpro',
    faviconUrl: null
  },
  accentColor: '#8B5CF6',
  pageTitle: 'Graphikpro'
};

const EmailSettingsPanel = () => {
  const [emailSettings, setEmailSettings] = useState({ sender_email: '', resend_api_key: '', notification_emails: '' });
  const [loadingEmail, setLoadingEmail] = useState(true);
  const [savingEmail, setSavingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/owner/email-settings');
        setEmailSettings({
          sender_email: res.data.sender_email || '',
          resend_api_key: res.data.resend_api_key_masked || res.data.resend_api_key || '',
          notification_emails: Array.isArray(res.data.notification_emails) ? res.data.notification_emails.join(', ') : ''
        });
      } catch (e) { /* use defaults */ }
      setLoadingEmail(false);
    };
    load();
  }, []);

  const handleSaveEmail = async () => {
    setSavingEmail(true);
    try {
      await api.put('/owner/email-settings', {
        sender_email: emailSettings.sender_email,
        resend_api_key: emailSettings.resend_api_key,
        notification_emails: emailSettings.notification_emails
      });
      toast.success('Email settings saved!');
    } catch (e) {
      toast.error('Failed to save');
    }
    setSavingEmail(false);
  };

  const handleTestEmail = async () => {
    if (!testTo.trim()) { toast.error('Enter an email to test'); return; }
    setTestingEmail(true);
    try {
      const res = await api.post('/owner/email-settings/test', { to: testTo });
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test email failed');
    }
    setTestingEmail(false);
  };

  if (loadingEmail) return <p className="text-sm text-muted-foreground py-4">Loading...</p>;

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <Label className="text-base font-semibold">Sender Email</Label>
        <p className="text-xs text-muted-foreground">The "from" address for all emails (must match your verified Resend domain)</p>
        <Input value={emailSettings.sender_email} onChange={(e) => setEmailSettings(p => ({ ...p, sender_email: e.target.value }))} placeholder="noreply@sub.graphikpro.com" data-testid="email-sender-input" />
      </div>
      <div className="space-y-1">
        <Label className="text-base font-semibold">Resend API Key</Label>
        <p className="text-xs text-muted-foreground">Leave unchanged to keep current key. Only update if you need a new key.</p>
        <Input value={emailSettings.resend_api_key} onChange={(e) => setEmailSettings(p => ({ ...p, resend_api_key: e.target.value }))} placeholder="re_..." className="font-mono" />
      </div>
      <div className="space-y-1">
        <Label className="text-base font-semibold">Notification Emails</Label>
        <p className="text-xs text-muted-foreground">Comma-separated emails that receive onboarding notifications</p>
        <Input value={emailSettings.notification_emails} onChange={(e) => setEmailSettings(p => ({ ...p, notification_emails: e.target.value }))} placeholder="admin@company.com, owner@company.com" />
      </div>
      <Button onClick={handleSaveEmail} disabled={savingEmail} data-testid="save-email-settings-btn">
        <Save className="w-4 h-4 mr-2" /> {savingEmail ? 'Saving...' : 'Save Email Settings'}
      </Button>

      <div className="border-t pt-4 space-y-2">
        <Label className="text-base font-semibold">Test Email</Label>
        <p className="text-xs text-muted-foreground">Send a test email to verify everything works</p>
        <div className="flex gap-2">
          <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="your@email.com" />
          <Button variant="outline" onClick={handleTestEmail} disabled={testingEmail}>
            <Send className="w-4 h-4 mr-2" /> {testingEmail ? 'Sending...' : 'Send Test'}
          </Button>
        </div>
      </div>

      {/* Gmail Integration */}
      <GmailIntegrationPanel />
    </div>
  );
};

const GmailIntegrationPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncHistory, setSyncHistory] = useState([]);

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    try {
      const [statusRes, historyRes] = await Promise.all([
        api.get('/gmail/status'),
        api.get('/gmail/sync-history').catch(() => ({ data: [] }))
      ]);
      setStatus(statusRes.data);
      setSyncHistory(historyRes.data);
    } catch (e) { /* not connected */ }
    setLoading(false);
  };

  const handleConnect = async () => {
    try {
      const res = await api.get('/gmail/auth-url');
      window.location.href = res.data.auth_url;
    } catch (e) {
      toast.error('Failed to get auth URL');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/gmail/sync');
      toast.success(res.data.message);
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    }
    setSyncing(false);
  };

  const handleDisconnect = async () => {
    if (!confirm('Disconnect Gmail?')) return;
    try {
      await api.post('/gmail/disconnect');
      toast.success('Gmail disconnected');
      setStatus({ connected: false });
    } catch (e) { toast.error('Failed'); }
  };

  if (loading) return null;

  return (
    <div className="border-t pt-5 space-y-4">
      <div>
        <Label className="text-base font-semibold">Gmail Live Sync</Label>
        <p className="text-xs text-muted-foreground">Connect Gmail to auto-import PO data from emails. Syncs every 5 minutes.</p>
      </div>

      {status?.connected ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Badge className="bg-green-100 text-green-700">Connected</Badge>
            <span className="text-xs text-muted-foreground">Since {status.connected_at ? new Date(status.connected_at).toLocaleDateString() : '—'}</span>
            {status.last_sync && (
              <span className="text-xs text-muted-foreground">Last sync: {new Date(status.last_sync).toLocaleString()} ({status.last_sync_count} imported)</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSync} disabled={syncing} size="sm" data-testid="gmail-sync-btn">
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleDisconnect} className="text-destructive">
              Disconnect
            </Button>
          </div>
          {syncHistory.length > 0 && (
            <div className="text-xs space-y-1 max-h-32 overflow-y-auto bg-muted/50 rounded-lg p-2">
              {syncHistory.slice(0, 5).map((log, i) => (
                <div key={i} className="flex justify-between">
                  <span>{new Date(log.synced_at).toLocaleString()}</span>
                  <span>{log.imported} imported, {log.duplicates || 0} dupes{log.auto_sync ? ' (auto)' : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <Button onClick={handleConnect} data-testid="gmail-connect-btn">
          Connect Gmail Account
        </Button>
      )}
    </div>
  );
};

const OwnerSettings = () => {
  const { user } = useOutletContext();
  const { themeMode, setThemeMode, animationsEnabled, setAnimationsEnabled } = useTheme();
  const [settings, setSettings] = useState(DEFAULT_THEME);
  const [originalSettings, setOriginalSettings] = useState(DEFAULT_THEME);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('appearance');

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    // Check for unsaved changes
    const changed = JSON.stringify(settings) !== JSON.stringify(originalSettings);
    setHasChanges(changed);
  }, [settings, originalSettings]);

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings');
      const data = {
        colors: { ...DEFAULT_THEME.colors, ...response.data.colors },
        fonts: { ...DEFAULT_THEME.fonts, ...response.data.fonts },
        assets: { ...DEFAULT_THEME.assets, ...response.data.assets },
        accentColor: response.data.accentColor || DEFAULT_THEME.accentColor,
        pageTitle: response.data.pageTitle || DEFAULT_THEME.pageTitle
      };
      setSettings(data);
      setOriginalSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/owner/settings', settings);
      setOriginalSettings(settings);
      toast.success('Settings saved successfully!');
      // Reload page to apply new theme
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Reset all settings to default? This cannot be undone.')) return;
    
    setSaving(true);
    try {
      await api.post('/owner/settings/reset');
      setSettings(DEFAULT_THEME);
      setOriginalSettings(DEFAULT_THEME);
      toast.success('Settings reset to default!');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    setSettings(originalSettings);
    toast.info('Reverted to last saved state');
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate
    if (!['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'].includes(file.type)) {
      toast.error('Invalid file type. Use PNG, JPG, SVG or WebP');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Maximum 2MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/owner/upload/logo', formData);
      setSettings(prev => ({
        ...prev,
        assets: { ...prev.assets, logoUrl: response.data.logoUrl }
      }));
      toast.success('Logo uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/owner/upload/favicon', formData);
      setSettings(prev => ({
        ...prev,
        assets: { ...prev.assets, faviconUrl: response.data.faviconUrl }
      }));
      toast.success('Favicon uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload favicon');
    } finally {
      setUploading(false);
    }
  };

  const updateColor = (key, value) => {
    setSettings(prev => ({
      ...prev,
      colors: { ...prev.colors, [key]: value }
    }));
  };

  const updateFont = (key, value) => {
    setSettings(prev => ({
      ...prev,
      fonts: { ...prev.fonts, [key]: value }
    }));
  };

  const updateAsset = (key, value) => {
    setSettings(prev => ({
      ...prev,
      assets: { ...prev.assets, [key]: value }
    }));
  };

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_BACKEND_URL || '';
  };

  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-bold text-primary">Website Settings</h1>
            <p className="text-muted-foreground">Customize your website's appearance</p>
          </div>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <Badge variant="outline" className="text-warning border-warning">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Unsaved Changes
              </Badge>
            )}
            <Button variant="outline" onClick={handleRevert} disabled={!hasChanges || saving}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Revert
            </Button>
            <Button variant="outline" onClick={handleReset} disabled={saving}>
              Reset to Default
            </Button>
            <Button onClick={handleSave} disabled={!hasChanges || saving || uploading}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Settings Panel */}
          <div className="lg:col-span-2">
            <Card>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <CardHeader>
                  <TabsList className="grid w-full grid-cols-6">
                    <TabsTrigger value="appearance" className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Theme
                    </TabsTrigger>
                    <TabsTrigger value="branding" className="flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      Branding
                    </TabsTrigger>
                    <TabsTrigger value="colors" className="flex items-center gap-2">
                      <Palette className="w-4 h-4" />
                      Colors
                    </TabsTrigger>
                    <TabsTrigger value="fonts" className="flex items-center gap-2">
                      <Type className="w-4 h-4" />
                      Fonts
                    </TabsTrigger>
                    <TabsTrigger value="email" className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email
                    </TabsTrigger>
                    <TabsTrigger value="columns" className="flex items-center gap-2">
                      <Columns className="w-4 h-4" />
                      Columns
                    </TabsTrigger>
                  </TabsList>
                </CardHeader>
                <CardContent>
                  {/* Theme/Appearance Tab */}
                  <TabsContent value="appearance" className="space-y-6">
                    <div>
                      <Label className="text-base font-semibold">Application Theme</Label>
                      <p className="text-sm text-muted-foreground mb-4">
                        Choose how Graphikpro looks for all users across the application
                      </p>
                      
                      <div className="grid grid-cols-3 gap-4">
                        {/* Light Theme */}
                        <button
                          onClick={() => {
                            setThemeMode('light');
                            toast.success('Light theme applied!');
                          }}
                          className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
                            themeMode === 'light' 
                              ? 'border-violet-500 bg-violet-50 shadow-lg shadow-violet-100' 
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                          data-testid="theme-light"
                        >
                          {themeMode === 'light' && (
                            <div className="absolute -top-2 -right-2 w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                          <div className="flex flex-col items-center gap-3">
                            <div className="w-16 h-16 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-sm">
                              <Sun className="w-8 h-8 text-amber-500" />
                            </div>
                            <div className="text-center">
                              <p className="font-semibold text-gray-900">Light</p>
                              <p className="text-xs text-gray-500">Clean & bright</p>
                            </div>
                          </div>
                          {/* Preview */}
                          <div className="mt-4 p-2 rounded-lg bg-white border border-gray-100">
                            <div className="h-2 w-12 bg-gray-200 rounded mb-1"></div>
                            <div className="h-2 w-8 bg-violet-500 rounded"></div>
                          </div>
                        </button>
                        
                        {/* Dark Theme */}
                        <button
                          onClick={() => {
                            setThemeMode('dark');
                            toast.success('Dark theme applied!');
                          }}
                          className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
                            themeMode === 'dark' 
                              ? 'border-violet-500 bg-violet-50 shadow-lg shadow-violet-100' 
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                          data-testid="theme-dark"
                        >
                          {themeMode === 'dark' && (
                            <div className="absolute -top-2 -right-2 w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                          <div className="flex flex-col items-center gap-3">
                            <div className="w-16 h-16 rounded-xl bg-slate-800 border border-slate-600 flex items-center justify-center shadow-sm">
                              <Moon className="w-8 h-8 text-slate-300" />
                            </div>
                            <div className="text-center">
                              <p className="font-semibold text-gray-900">Dark</p>
                              <p className="text-xs text-gray-500">Easy on eyes</p>
                            </div>
                          </div>
                          {/* Preview */}
                          <div className="mt-4 p-2 rounded-lg bg-slate-800 border border-slate-700">
                            <div className="h-2 w-12 bg-slate-600 rounded mb-1"></div>
                            <div className="h-2 w-8 bg-violet-400 rounded"></div>
                          </div>
                        </button>
                        
                        {/* Glass Theme */}
                        <button
                          onClick={() => {
                            setThemeMode('glass');
                            toast.success('Liquid Glass theme applied!');
                          }}
                          className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
                            themeMode === 'glass' 
                              ? 'border-violet-500 bg-violet-50 shadow-lg shadow-violet-100' 
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                          data-testid="theme-glass"
                        >
                          {themeMode === 'glass' && (
                            <div className="absolute -top-2 -right-2 w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                          <div className="flex flex-col items-center gap-3">
                            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 border border-white/50 flex items-center justify-center shadow-lg" style={{ backdropFilter: 'blur(8px)' }}>
                              <Sparkles className="w-8 h-8 text-violet-500" />
                            </div>
                            <div className="text-center">
                              <p className="font-semibold text-gray-900">Liquid Glass</p>
                              <p className="text-xs text-gray-500">Apple-style blur</p>
                            </div>
                          </div>
                          {/* Preview */}
                          <div className="mt-4 p-2 rounded-lg bg-gradient-to-br from-white/80 to-violet-50/50 border border-white/50">
                            <div className="h-2 w-12 bg-white/50 rounded mb-1"></div>
                            <div className="h-2 w-8 bg-violet-400/80 rounded"></div>
                          </div>
                        </button>
                      </div>
                      
                      <div className="mt-6 p-4 bg-muted/50 rounded-xl">
                        <p className="text-sm text-muted-foreground">
                          <strong>Note:</strong> Theme selection affects all users across the entire application. 
                          The selected theme is saved in browser storage for instant loading.
                        </p>
                      </div>

                      {/* Animations Toggle */}
                      <div className="mt-6 p-4 border rounded-xl">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                              <Zap className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                              <Label className="text-base font-semibold cursor-pointer" htmlFor="animations-toggle">
                                Animations & Transitions
                              </Label>
                              <p className="text-sm text-muted-foreground">
                                {animationsEnabled ? 'Enabled — smooth page transitions and hover effects' : 'Disabled — faster page loads, no motion'}
                              </p>
                            </div>
                          </div>
                          <Switch
                            id="animations-toggle"
                            data-testid="animations-toggle"
                            checked={animationsEnabled}
                            onCheckedChange={(checked) => {
                              setAnimationsEnabled(checked);
                              toast.success(checked ? 'Animations enabled' : 'Animations disabled — pages will load faster');
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                  
                  {/* Branding Tab */}
                  <TabsContent value="branding" className="space-y-6">
                    <div className="space-y-4">
                      <div>
                        <Label className="text-base font-semibold">Logo</Label>
                        <p className="text-sm text-muted-foreground mb-3">
                          Upload your company logo. Recommended: 200x60px or wider. Supported: PNG, JPG, SVG, WebP (max 2MB)
                        </p>
                        <div className="flex items-start gap-4">
                          <div className="w-40 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/50">
                            {settings.assets.logoUrl ? (
                              <img 
                                src={`${getApiBaseUrl()}${settings.assets.logoUrl}`}
                                alt={settings.assets.logoAlt}
                                className="max-w-full max-h-full object-contain p-2"
                              />
                            ) : (
                              <span className="text-muted-foreground text-sm">No logo</span>
                            )}
                          </div>
                          <div className="space-y-2">
                            <input
                              type="file"
                              id="logo-upload"
                              className="hidden"
                              accept="image/png,image/jpeg,image/svg+xml,image/webp"
                              onChange={handleLogoUpload}
                            />
                            <Button 
                              variant="outline" 
                              onClick={() => document.getElementById('logo-upload').click()}
                              disabled={uploading}
                            >
                              <Upload className="w-4 h-4 mr-2" />
                              {uploading ? 'Uploading...' : 'Upload Logo'}
                            </Button>
                            {settings.assets.logoUrl && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => updateAsset('logoUrl', null)}
                              >
                                Remove
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Logo Alt Text</Label>
                        <Input
                          value={settings.assets.logoAlt || ''}
                          onChange={(e) => updateAsset('logoAlt', e.target.value)}
                          placeholder="Company name"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Page Title (Browser Tab)</Label>
                        <Input
                          value={settings.pageTitle || ''}
                          onChange={(e) => setSettings(prev => ({ ...prev, pageTitle: e.target.value }))}
                          placeholder="Graphikpro"
                        />
                        <p className="text-xs text-muted-foreground">This appears in the browser tab</p>
                      </div>

                      <div className="pt-4 border-t">
                        <Label className="text-base font-semibold">Favicon</Label>
                        <p className="text-sm text-muted-foreground mb-3">
                          Upload a favicon. Supported: PNG, ICO, SVG (max 512KB)
                        </p>
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/50">
                            {settings.assets.faviconUrl ? (
                              <img 
                                src={`${getApiBaseUrl()}${settings.assets.faviconUrl}`}
                                alt="Favicon"
                                className="w-8 h-8 object-contain"
                              />
                            ) : (
                              <span className="text-muted-foreground text-xs">32x32</span>
                            )}
                          </div>
                          <div>
                            <input
                              type="file"
                              id="favicon-upload"
                              className="hidden"
                              accept="image/png,image/x-icon,image/svg+xml"
                              onChange={handleFaviconUpload}
                            />
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => document.getElementById('favicon-upload').click()}
                              disabled={uploading}
                            >
                              <Upload className="w-4 h-4 mr-2" />
                              Upload Favicon
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Colors Tab */}
                  <TabsContent value="colors" className="space-y-6">
                    {/* Accent Color - Primary Feature */}
                    <div className="space-y-3">
                      <Label className="text-base font-semibold">Accent Color</Label>
                      <p className="text-sm text-muted-foreground">
                        This color is used for gradients, highlights, buttons and links across the app.
                      </p>
                      <div className="flex items-center gap-4">
                        <input
                          type="color"
                          value={settings.accentColor}
                          onChange={(e) => setSettings(prev => ({ ...prev, accentColor: e.target.value }))}
                          className="w-16 h-12 rounded-lg border-2 cursor-pointer"
                          data-testid="accent-color-picker"
                        />
                        <Input
                          value={settings.accentColor}
                          onChange={(e) => setSettings(prev => ({ ...prev, accentColor: e.target.value }))}
                          placeholder="#8B5CF6"
                          className="font-mono uppercase w-32"
                          maxLength={7}
                        />
                        <div 
                          className="h-10 flex-1 rounded-lg"
                          style={{ background: `linear-gradient(135deg, ${settings.accentColor}, ${settings.accentColor}cc)` }}
                        />
                      </div>
                      {/* Quick presets */}
                      <div className="flex gap-2 flex-wrap">
                        {['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#06B6D4', '#F97316'].map(color => (
                          <button
                            key={color}
                            onClick={() => setSettings(prev => ({ ...prev, accentColor: color }))}
                            className="w-8 h-8 rounded-full border-2 transition-transform hover:scale-110"
                            style={{ 
                              backgroundColor: color,
                              borderColor: settings.accentColor === color ? '#000' : 'transparent'
                            }}
                            title={color}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <Label className="text-base font-semibold mb-3 block">Theme Colors</Label>
                      <div className="grid grid-cols-2 gap-4">
                        {Object.entries(settings.colors).map(([key, value]) => (
                          <div key={key} className="space-y-2">
                            <Label className="capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</Label>
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={value}
                                onChange={(e) => updateColor(key, e.target.value)}
                                className="w-12 h-10 rounded border cursor-pointer"
                              />
                              <Input
                                value={value}
                                onChange={(e) => updateColor(key, e.target.value)}
                                placeholder="#RRGGBB"
                                className="font-mono uppercase"
                                maxLength={7}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </TabsContent>

                  {/* Fonts Tab */}
                  <TabsContent value="fonts" className="space-y-6">
                    <div className="space-y-4">
                      <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                        <strong>Note:</strong> "Degular" font is not available on Google Fonts (it's Adobe Fonts exclusive). 
                        We've included close alternatives like <em>Plus Jakarta Sans</em>, <em>Urbanist</em>, <em>Sora</em>, and <em>Space Grotesk</em>.
                      </div>
                      <div className="space-y-2">
                        <Label className="text-base font-semibold">Heading Font</Label>
                        <p className="text-sm text-muted-foreground">Used for titles and headings</p>
                        <Select 
                          value={settings.fonts.headingFont} 
                          onValueChange={(v) => updateFont('headingFont', v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {HEADING_FONTS.map(font => (
                              <SelectItem key={font} value={font} style={{ fontFamily: font }}>
                                {font}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-lg mt-2 border rounded-lg p-3" style={{ fontFamily: settings.fonts.headingFont }}>
                          The quick brown fox jumps over the lazy dog
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-base font-semibold">Body Font</Label>
                        <p className="text-sm text-muted-foreground">Used for paragraphs and general text</p>
                        <Select 
                          value={settings.fonts.bodyFont} 
                          onValueChange={(v) => updateFont('bodyFont', v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {BODY_FONTS.map(font => (
                              <SelectItem key={font} value={font} style={{ fontFamily: font }}>
                                {font}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-sm mt-2 border rounded-lg p-3" style={{ fontFamily: settings.fonts.bodyFont }}>
                          The quick brown fox jumps over the lazy dog. 0123456789
                        </p>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Email Settings Tab */}
                  <TabsContent value="email" className="space-y-4">
                    <EmailSettingsPanel />
                  </TabsContent>

                  <TabsContent value="columns" className="space-y-4">
                    <div className="space-y-4">
                      <div>
                        <Label className="text-base font-semibold">Client Billings Column Visibility</Label>
                        <p className="text-sm text-muted-foreground mb-4">
                          Choose which columns to show in the Client Billings table. You can also access this from the "Columns" button on the Client Billings page.
                        </p>
                        <div className="p-4 bg-muted/50 rounded-lg">
                          <p className="text-sm text-muted-foreground mb-2">
                            Go to <strong>Client Billings</strong> page and click the <strong>"Columns"</strong> button to customize which columns appear in your table.
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Your preferences will be saved automatically and persist across sessions.
                          </p>
                        </div>
                      </div>
                      <div className="pt-4 border-t">
                        <Label className="text-base font-semibold">Quick Tips</Label>
                        <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
                          <li>• Default columns: PO Number, PO Status, Amount, Client, Main Title, Task, Due On, Accepted On</li>
                          <li>• Hide columns you don't need to reduce clutter</li>
                          <li>• Rows with pending acceptance status are highlighted in orange</li>
                          <li>• Status badges: <Badge className="bg-green-500 text-white text-xs ml-1">Accepted</Badge> <Badge className="bg-blue-500 text-white text-xs ml-1">Completed</Badge> <Badge className="bg-yellow-500 text-black text-xs ml-1">Issued</Badge> <Badge className="bg-red-500 text-white text-xs ml-1">Canceled</Badge></li>
                        </ul>
                      </div>
                    </div>
                  </TabsContent>
                </CardContent>
              </Tabs>
            </Card>
          </div>

          {/* Live Preview */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Live Preview
                </CardTitle>
                <CardDescription>See how your changes will look</CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className="rounded-lg border overflow-hidden"
                  style={{ 
                    backgroundColor: settings.colors.background,
                    fontFamily: settings.fonts.bodyFont
                  }}
                >
                  {/* Preview Header */}
                  <div 
                    className="p-4 border-b"
                    style={{ 
                      backgroundColor: settings.colors.surface,
                      borderColor: settings.colors.border
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {settings.assets.logoUrl ? (
                        <img 
                          src={`${getApiBaseUrl()}${settings.assets.logoUrl}`}
                          alt="Logo"
                          className="h-6 object-contain"
                        />
                      ) : (
                        <div 
                          className="font-bold text-lg"
                          style={{ 
                            color: settings.colors.primary,
                            fontFamily: settings.fonts.headingFont
                          }}
                        >
                          Graphikpro
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Preview Content */}
                  <div className="p-4 space-y-4">
                    <h3 
                      className="text-lg font-bold"
                      style={{ 
                        color: settings.colors.text,
                        fontFamily: settings.fonts.headingFont
                      }}
                    >
                      Sample Heading
                    </h3>
                    <p style={{ color: settings.colors.mutedText, fontSize: '14px' }}>
                      This is sample body text showing how your content will appear.
                    </p>
                    
                    {/* Sample Buttons */}
                    <div className="flex gap-2 flex-wrap">
                      <button
                        className="px-4 py-2 rounded-md text-white text-sm font-medium"
                        style={{ backgroundColor: settings.colors.primary }}
                      >
                        Primary
                      </button>
                      <button
                        className="px-4 py-2 rounded-md text-white text-sm font-medium"
                        style={{ backgroundColor: settings.colors.secondary }}
                      >
                        Secondary
                      </button>
                      <button
                        className="px-4 py-2 rounded-md text-white text-sm font-medium"
                        style={{ backgroundColor: settings.colors.accent }}
                      >
                        Accent
                      </button>
                    </div>

                    {/* Sample Card */}
                    <div 
                      className="p-3 rounded-md"
                      style={{ 
                        backgroundColor: settings.colors.surface,
                        border: `1px solid ${settings.colors.border}`
                      }}
                    >
                      <p 
                        className="text-sm font-medium"
                        style={{ color: settings.colors.text }}
                      >
                        Sample Card
                      </p>
                      <p 
                        className="text-xs mt-1"
                        style={{ color: settings.colors.mutedText }}
                      >
                        Card description text
                      </p>
                    </div>

                    {/* Sample Link */}
                    <a 
                      href="#" 
                      className="text-sm underline"
                      style={{ color: settings.colors.primary }}
                      onClick={(e) => e.preventDefault()}
                    >
                      Sample link text
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OwnerSettings;
